import React from 'react';
import classnames from 'classnames';
import styles from './CartIcon.module.scss';

const PRIMARY_1 = '#d7be96';

export type CartIconProps = {
  size?: number;
  color?: string;
  className?: string;
  count?: number;
  badgeColor?: string;
  dataTestId?: string;
};

/**
 * Cart icon with optional count badge overlay.
 * @component
 * @param {CartIconProps} props
 * @returns {JSX.Element}
 * @example
 * <CartIcon size={24} color="white" count={3} />
 */
export const CartIcon: React.FC<CartIconProps> = ({
  size = 20,
  color = 'currentColor',
  className,
  count,
  badgeColor = PRIMARY_1,
  dataTestId = 'cart-icon',
}) => {
  const badgeSize = Math.max(14, Math.round(size * 0.5));
  const badgeFontSize =
    count !== undefined && count > 9
      ? `${Math.round(badgeSize * 0.56)}px`
      : `${Math.round(badgeSize * 0.62)}px`;
  const badgeTop = Math.round(size * 0.04);
  const badgeRight = Math.round(badgeSize * 0.15);

  return (
    <span className={classnames(styles.wrapper, className)}>
      <svg
        data-testid={dataTestId}
        width={size}
        height={size}
        viewBox="0 0 28 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <polyline
          points="7,9 21,9 19,16.5 9,16.5 7,9"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <polyline
          points="7,9 6.5,7 4,7"
          stroke={color}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <circle cx="9.5" cy="20.5" r="1.5" stroke={color} strokeWidth="1.5" fill="none" />
        <circle cx="18.5" cy="20.5" r="1.5" stroke={color} strokeWidth="1.5" fill="none" />
      </svg>
      {count !== undefined && count > 0 && (
        <span
          data-testid={`${dataTestId}-badge`}
          className={styles.badge}
          // eslint-disable-next-line react/forbid-dom-props -- dynamic badge sizing and colour from size/count/badgeColor props
          style={
            {
              '--badge-size': `${badgeSize}px`,
              '--badge-top': `${badgeTop}px`,
              '--badge-right': `${badgeRight}px`,
              '--badge-bg': badgeColor,
              '--badge-font-size': badgeFontSize,
            } as React.CSSProperties
          }
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </span>
  );
};

export default CartIcon;
